import {Params} from '@angular/router';
import {normalizeProductQueryParams, ProductQueryParams, ProductQueryParamsInput} from '../../../models';

const DEFAULT_LIST_SORT: Pick<ProductQueryParams, 'sortBy' | 'direction'> = {
  sortBy: 'created_at',
  direction: 'desc',
};

/**
 * Builds cleaned product query params with defaults and only allowed keys.
 */
export function buildCleanedParams(queryParams: Partial<ProductQueryParams>): ProductQueryParams {
  const apiParams = normalizeProductQueryParams(queryParams as ProductQueryParamsInput);
  const cleaned: ProductQueryParams = {
    page: apiParams.page,
    take: apiParams.take,
  };

  if (apiParams.query !== undefined) {
    cleaned.query = apiParams.query;
  }
  if (apiParams.status !== undefined) {
    cleaned.status = apiParams.status;
  }
  if (apiParams.active !== undefined) {
    cleaned.active = apiParams.active;
  }
  if (apiParams.catalogId !== undefined) {
    cleaned.catalogId = apiParams.catalogId;
  }
  if (apiParams.sortBy !== undefined) {
    cleaned.sortBy = apiParams.sortBy;
  }
  if (apiParams.direction !== undefined) {
    cleaned.direction = apiParams.direction;
  }

  return cleaned;
}

export function withDefaultListSort(queryParams: ProductQueryParams): ProductQueryParams {
  return queryParams.sortBy ? queryParams : {...queryParams, ...DEFAULT_LIST_SORT};
}

export function buildListApiQueryParams(queryParams: Partial<ProductQueryParams>): ProductQueryParams {
  return withDefaultListSort(buildCleanedParams(queryParams));
}

export function buildSelectionListApiQueryParams(queryParams: Partial<ProductQueryParams>): ProductQueryParams {
  return buildCleanedParams(queryParams);
}

export function buildProductListQueryParams(params: Params): ProductQueryParams {
  return normalizeProductQueryParams({
    page: params['page'],
    take: params['take'],
    query: params['query'],
    status: params['status'],
    catalogId: params['catalogId'] ?? params['catalog_id'],
    sortBy: params['sortBy'],
    direction: params['direction'],
  } as ProductQueryParamsInput);
}

export function buildProductListUrlQueryParams(queryParams: ProductQueryParams): Params {
  return {
    page: queryParams.page,
    take: queryParams.take,
    query: queryParams.query || undefined,
    status: queryParams.status ?? activeToStatus(queryParams.active),
    catalogId: queryParams.catalogId,
    sortBy: queryParams.sortBy || undefined,
    direction: queryParams.direction || undefined,
  };
}

export function missingDefaultProductQueryParams(params: Params): boolean {
  return params['page'] === undefined && params['take'] === undefined;
}

/**
 * Merges incoming query params with the current URL params, only overriding the
 * fields that are explicitly present in `incoming` (using `hasOwnProperty` so an
 * explicit `undefined` still counts as "I want to clear this").
 *
 * Used by the product list and bundle list when the user changes search,
 * pagination, or sort to keep the rest of the URL state intact.
 */
export function mergeProductQueryParams(incoming: ProductQueryParams, current: ProductQueryParams): ProductQueryParams {
  const has = (key: keyof ProductQueryParams) => Object.prototype.hasOwnProperty.call(incoming, key);
  return {
    page: has('page') ? incoming.page : current.page,
    take: has('take') ? incoming.take : current.take,
    query: has('query') ? incoming.query : current.query,
    status: has('status') ? incoming.status : current.status,
    active: has('active') ? incoming.active : current.active,
    catalogId: has('catalogId') ? incoming.catalogId : current.catalogId,
    sortBy: has('sortBy') ? incoming.sortBy : current.sortBy,
    direction: has('direction') ? incoming.direction : current.direction,
  };
}

function activeToStatus(active: boolean | undefined): ProductQueryParams['status'] {
  if (active === true) {
    return 'active';
  }

  if (active === false) {
    return 'inactive';
  }

  return undefined;
}
